import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tab", "controls0", "controls1", "controls2",
    "periodSelect", "diasSelect", "dateSelect",
    "canvas", "empty", "subtitle"
  ]
  static values = { cotacoes: Array }

  connect() {
    this.chartInstance = null
    this.currentTab = 0
    if (this.cotacoesValue.length > 0) {
      this.populateSelects()
      this.render()
    }
  }

  disconnect() {
    this.chartInstance?.destroy()
  }

  switchTab(event) {
    const tab = parseInt(event.currentTarget.dataset.tab)
    this.currentTab = tab

    this.tabTargets.forEach((el, i) => el.classList.toggle("active", i === tab))

    const controls = [this.controls0Target, this.controls1Target, this.controls2Target]
    controls.forEach((el, i) => { el.style.display = i === tab ? "flex" : "none" })

    this.render()
  }

  updateChart() { this.render() }

  render() {
    if (this.currentTab === 0) this.renderMedias()
    else if (this.currentTab === 1) this.renderEvolucao()
    else this.renderSnapshot()
  }

  // Chart 1 — avg preco_dia per trip duration
  renderMedias() {
    const period = this.periodSelectTarget.value
    const data = this.filterByPeriod(period)

    const groups = {}
    data.forEach(c => {
      if (c.preco_dia <= 0) return
      if (!groups[c.dias]) groups[c.dias] = []
      groups[c.dias].push(c.preco_dia)
    })

    const dias = Object.keys(groups).map(Number).sort((a, b) => a - b)
    const avgs = dias.map(d => +(groups[d].reduce((s, v) => s + v, 0) / groups[d].length).toFixed(2))

    const periodLabel = period === "all" ? "todo o período" : `últimos ${period} dias`
    this.draw(
      { type: "bar", labels: dias.map(d => `${d}d`), datasets: [{ label: "Média R$/dia", data: avgs, backgroundColor: "rgba(134,40,253,0.75)", borderColor: "#8628fd", borderWidth: 1, borderRadius: 5, borderSkipped: false }] },
      `Média do preço/dia por duração · ${periodLabel}`
    )
  }

  // Chart 2 — preco_dia over time for a specific duration
  renderEvolucao() {
    const selectedDias = parseInt(this.diasSelectTarget.value)
    if (!selectedDias) return
    const pts = this.cotacoesValue
      .filter(c => c.dias === selectedDias && c.preco_dia > 0)
      .sort((a, b) => a.data.localeCompare(b.data))

    this.draw(
      {
        type: "line",
        labels: pts.map(c => this.fmtDate(c.data)),
        datasets: [{
          label: `R$/dia`,
          data: pts.map(c => c.preco_dia),
          borderColor: "#8628fd",
          backgroundColor: "rgba(134,40,253,0.06)",
          fill: true,
          pointBackgroundColor: "#8628fd",
          pointRadius: pts.length < 15 ? 5 : 3,
          pointHoverRadius: 7,
          borderWidth: 2,
          tension: 0.35
        }]
      },
      `Evolução do preço/dia para viagens de ${selectedDias} dias`
    )
  }

  // Chart 3 — preco_dia per duration for a specific date
  renderSnapshot() {
    const selectedDate = this.dateSelectTarget.value
    if (!selectedDate) return
    const pts = this.cotacoesValue
      .filter(c => c.data === selectedDate && c.preco_dia > 0)
      .sort((a, b) => a.dias - b.dias)

    this.draw(
      {
        type: "bar",
        labels: pts.map(c => `${c.dias}d`),
        datasets: [{ label: "R$/dia", data: pts.map(c => c.preco_dia), backgroundColor: "rgba(134,40,253,0.75)", borderColor: "#8628fd", borderWidth: 1, borderRadius: 5, borderSkipped: false }]
      },
      `Preços coletados em ${this.fmtDate(selectedDate)}`
    )
  }

  draw(config, subtitle) {
    this.chartInstance?.destroy()
    const ctx = this.canvasTarget.getContext("2d")
    this.chartInstance = new Chart(ctx, {
      type: config.type,
      data: { labels: config.labels, datasets: config.datasets },
      options: this.opts()
    })
    if (this.hasSubtitleTarget) this.subtitleTarget.textContent = subtitle
  }

  opts() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 220 },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1e293b", borderColor: "#334155", borderWidth: 1,
          titleColor: "#f1f5f9", bodyColor: "#94a3b8",
          padding: 10, cornerRadius: 6,
          titleFont: { family: "Inter", weight: "600", size: 12 },
          bodyFont: { family: "Inter", size: 12 },
          callbacks: { label: ctx => ` R$ ${(ctx.parsed.y ?? 0).toFixed(2)}/dia` }
        }
      },
      scales: {
        x: {
          ticks: { color: "#94a3b8", font: { family: "Inter", size: 11 }, maxRotation: 0, maxTicksLimit: 12 },
          grid: { color: "rgba(100,116,139,0.07)" },
          border: { display: false }
        },
        y: {
          ticks: { color: "#94a3b8", font: { family: "Inter", size: 11 }, callback: v => `R$${v}` },
          grid: { color: "rgba(100,116,139,0.07)" },
          border: { display: false },
          beginAtZero: false,
          title: { display: false }
        }
      }
    }
  }

  populateSelects() {
    const all = this.cotacoesValue
    const dias = [...new Set(all.map(c => c.dias))].sort((a, b) => a - b)
    this.diasSelectTarget.innerHTML = dias.map(d => `<option value="${d}">${d} dias</option>`).join("")
    const dates = [...new Set(all.map(c => c.data))].sort().reverse()
    this.dateSelectTarget.innerHTML = dates
      .map((d, i) => `<option value="${d}">${this.fmtDate(d)}${i === 0 ? " ← mais recente" : ""}</option>`)
      .join("")
  }

  filterByPeriod(period) {
    if (period === "all") return this.cotacoesValue
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - parseInt(period))
    const cutoffStr = cutoff.toISOString().split("T")[0]
    return this.cotacoesValue.filter(c => c.data >= cutoffStr)
  }

  fmtDate(str) {
    const [y, m, d] = str.split("-")
    return `${d}/${m}`
  }

}
